import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, Link, useParams } from 'react-router-dom';
import useBoundStore from 'store';
import moment from 'moment-timezone';
import { ApartmentDeal } from '../../api/hooks/use-apartment-deals';
import useApartment from 'api/hooks/use-apartment';
import {
  createCallRequest,
  toggleStatusNotification,
  toggleTemporary,
  toggleNotification,
  generateSecondaryApartmentReport,
} from 'api';
import {
  toReadablePrice,
  formatNullishValue,
  toReadableSquarePrice,
  formatApartmentDescAlternative,
  extractYear,
} from 'lib/utils';
import SecondaryApartmentActions from '../../components/secondary-apartment-actions';
import Loader from 'components/loader';
import BaseScreen from 'components/base-screen';
import IconButton from 'components/icon-button';
import DottedList from 'components/dotted-list';
import ErrorMessage from 'components/error-message';
import ImageCarousel from 'components/image-carousel';
import Button, { ButtonProps } from 'components/button';
import heart from 'assets/images/heart.svg';
import threeDots from 'assets/images/three-dots.svg';
import temporaryYellow from '../../assets/images/temporary-yellow.svg';
import temporary from '../../assets/images/temporary.svg';
import bellFilled from '../../assets/images/bell-filled.svg';
import bell from '../../assets/images/bell.svg';
import styles from './styles.module.scss';
import AddFacilityToFavoriteListBottomSheet from 'components/favorite-list-item/modals/addFacilityToFavoriteListBottomSheet';
import { MarketTypes } from 'api/types';
import Estimate from 'screens/estimate';
import ItemsYmap from 'components/items-ymap';
import useGetMarketPrice from './useGetMarketPrice';
import ModalSupportDeal from 'screens/estimate/components/modals/modalAnalogs/inSystem/modalSupportDeal';
import { toast } from 'react-toastify';
import { BuyApartmentFlowPaths } from 'routes';
import { WallMaterial } from 'lib/dictionary';

export type ApartmentNavigateProps = {
  id: number;
  isTemporary?: boolean;
};

export const SecondaryApartmentBuy = () => {
  const navigate = useNavigate();
  const forceAuthorize = useBoundStore((s) => s.forceAuthorize);
  const showToast = useBoundStore((s) => s.showToast);
  const apartmentId = (useLocation().state as ApartmentNavigateProps).id;
  const { apartment, isLoading, error, mutate } = useApartment(apartmentId);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isReportGenerated, setIsReportGenerated] = useState(false);
  const [isCallButtonLoading, setIsCallButtonIsLoading] = useState(false);
  const [isCallButtonDisabled, setIsCallButtonDisabled] = useState(false);
  const [isFavBottomSheetOpen, setIsFavBottomSheetOpen] = useState(false);
  const [currentTimeZone, setCurrentTimeZone] = useState<string>('');
  const lastSelectedFacilityList = useBoundStore((s) => s.lastSelectedFacilityList);
  const user = useBoundStore((s) => s.user);
  const isClient = useMemo(() => user?.type === 'Client', [user]);
  useEffect(() => {
    // Getting the current browser time zone
    const browserTimeZone: string = moment(new Date()).format('YYYY-MM-DDTHH:mm:ss.SSSZ');
    setCurrentTimeZone(browserTimeZone);
  }, []);

  const [isOpenModalSupport, setIsOpenModalSupport] = useState(false);

  const handleChangeModalSupport = (value: boolean) => {
    setIsOpenModalSupport(value);
  };

  const { data, isMutating, onSubmit } = useGetMarketPrice(
    handleChangeModalSupport,
    apartment?.marketType || MarketTypes.SECONDARY,
    apartmentId,
  );
  const apartmentIsLoaded = !error && !isLoading;

  const buttonActions: ButtonProps[] = [];

  if (!data) {
    buttonActions.push({
      label: 'Узнать рыночную стоимость',
      onClick: () => onSubmit(),
      loading: isMutating,
      disabled: Boolean(data),
      variant: 'secondary',
    });
  }

  let buttons: ButtonProps[] = [];
  if (apartmentIsLoaded) {
    buttons = buttonActions;
  } else if (error) {
    buttons = [
      {
        label: 'Назад',
        onClick: () => navigate(-1),
      },
    ];
  }

  const handleNotificationToggle = (apartment: ApartmentDeal) => async (e: React.MouseEvent) => {
    e.stopPropagation();
    await toggleNotification(apartment.id, mutate);
  };

  const handleCreateCall = async () => {
    setIsCallButtonIsLoading(true);
    const { result } = await createCallRequest();
    if (result) {
      setIsCallButtonDisabled(true);
      showToast({ message: 'Свяжемся в течение 15 минут' });
    }
    setIsCallButtonIsLoading(false);
  };

  const handleFav = async () => {
    setIsFavBottomSheetOpen(true);
  };

  const handleTemporaryToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (apartment) {
      await toggleTemporary(apartment, mutate);
    }
  };

  const handleCreateReport = async () => {
    if (!isReportGenerated && forceAuthorize()) {
      setIsGeneratingReport(true);
      const { result } = await generateSecondaryApartmentReport(
        {
          id: apartmentId,
        },
        lastSelectedFacilityList,
      );
      setIsReportGenerated(!!result);
      showToast({ message: result ? 'Отчёт отправлен в чат-бот' : 'Ошибка отправки отчёта' });
      setIsGeneratingReport(false);
    }
  };

  const handleTrackPrice = (id: number) => {
    toggleStatusNotification(id).then((result) => {
      if (!!result?.result) {
        mutate();
        showToast({ message: 'Добавлен в уведомления' });
      }
    });
  };

  const apartmentDesc = apartment
    ? formatApartmentDescAlternative(apartment.rooms, apartment.area, apartment.floor)
    : '';

  return (
    <BaseScreen
      buttons={buttons}
      headerClassName={apartmentIsLoaded ? styles.header : ''}
      controls={
        <>
          <IconButton
            /*@ts-ignore*/
            onClick={handleNotificationToggle(apartment)}
            icon={apartment?.notificationEnabled ? bellFilled : bell}
          />
          {!isClient ? (
            <>
              <IconButton
                onClick={handleTemporaryToggle}
                /*@ts-ignore*/
                icon={apartment?.isSavedInTemporaryFiles ? temporaryYellow : temporary}
              />
              <IconButton icon={heart} onClick={handleFav} />
            </>
          ) : (
            <></>
          )}
        </>
      }
    >
      {apartment && !error ? (
        <>
          <ImageCarousel
            topPadding
            images={apartment?.images}
            isCustomSwipeIcon
            isCarouselInDetails
            isSecondary
          />

          <div className={styles.container}>
            {apartment.title && (
              <div className={styles.address}>
                <span className={styles.housingEstateName}>{apartment.housingEstate}</span>
                <span>{', ' + apartment.title}</span>
              </div>
            )}

            <div className={styles.prices}>
              <span className={styles.price}>{toReadablePrice(apartment.price)}</span>
              <span className={styles.squarePrice}>
                {toReadableSquarePrice(apartment.squarePrice)}
              </span>
            </div>

            <div className={styles.priceDynamicsContainer}>
              <Button
                variant='transparent'
                label={
                  apartment?.notificationEnabled ? 'Перестать следить за ценой' : 'Следить за ценой'
                }
                className={styles.priceSubscribeButton}
                onClick={() => handleTrackPrice(apartment?.id)}
              />
            </div>

            {apartment?.platformAd?.url && (
              <div className={styles.source}>
                <Link target={'_blank'} to={apartment?.platformAd?.url} title='Источник'>
                  {`Перейти в источник`}
                </Link>
              </div>
            )}

            <div className={styles.buttons}>
              <Button
                variant='gray'
                onClick={handleCreateReport}
                loading={isGeneratingReport}
                label={isReportGenerated ? 'Отчёт сформирован' : 'Сформировать отчёт'}
              />

              <IconButton
                icon={threeDots}
                iconHeight={24}
                iconWidth={24}
                className={styles.dotsButton}
                onClick={() => {
                  toast.dismiss();
                  setIsBottomSheetOpen(true);
                }}
              />
            </div>

            <DottedList
              header='Данные о квартире'
              items={[
                {
                  label: 'Материал стен',
                  value:
                    (apartment?.building?.wallMaterial &&
                      WallMaterial.label(apartment?.building?.wallMaterial)) ??
                    'Неизвестно',
                },
              ]}
              className={styles.apartmentInfoList}
            />

            <DottedList
              header='Данные о доме'
              items={[
                {
                  label: 'Название ЖК',
                  value: formatNullishValue(apartment.housingEstate),
                },
                {
                  label: 'Дом',
                  value: formatNullishValue(apartment.building.title),
                },
                {
                  label: 'Год сдачи',
                  value: !!apartment.building.commissioningQuarter
                    ? formatNullishValue(extractYear(apartment.building.commissioningQuarter))
                    : '',
                },
              ]}
            />

            <div className={styles.map}>
              <ItemsYmap
                items={
                  apartment?.building?.coordinates
                    ? [
                        {
                          id: 1,
                          coords: [
                            apartment?.building?.coordinates?.latitude,
                            apartment?.building?.coordinates?.longitude,
                          ],
                          selected: true,
                        },
                      ]
                    : []
                }
                isDisableDrag
                zoom={apartment?.building?.coordinates ? 15 : 11}
                isSetCenterByFirstItem={apartment?.building?.coordinates ? true : false}
              />
            </div>

            {data && apartment.id && (
              <div className={styles.estimate}>
                <Estimate
                  dataById={{ ...data, address: apartment.housingEstate }}
                  marketType={apartment.marketType ?? MarketTypes.SECONDARY}
                  facilityId={apartment.id}
                />
              </div>
            )}
          </div>

          <SecondaryApartmentActions
            open={isBottomSheetOpen}
            onDismiss={() => setIsBottomSheetOpen(false)}
          />
          <AddFacilityToFavoriteListBottomSheet
            onDismiss={() => setIsFavBottomSheetOpen(false)}
            isOpen={isFavBottomSheetOpen}
            marketType={apartment?.marketType as MarketTypes}
            facilityIds={[apartment.id]}
          />
        </>
      ) : isLoading ? (
        <Loader />
      ) : (
        <ErrorMessage message={error} />
      )}

      {apartmentId && (
        <ModalSupportDeal
          isOpen={isOpenModalSupport}
          facilityId={apartmentId}
          onDismiss={handleChangeModalSupport}
          title={'Заявка на расчет'}
          type='support'
          marketType={MarketTypes.SECONDARY}
        />
      )}
    </BaseScreen>
  );
};

export const SecondaryApartmentNavigation: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    let id = Number(params.id);
    if (id) {
      navigate(BuyApartmentFlowPaths.apartmentSecondary, {
        state: { id: id },
        replace: true,
      });
    }
  }, [params]);
  return <></>;
};
